import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Users, Plus, ChevronRight, Trash2, ShoppingCart, AlertTriangle } from 'lucide-react';

export const InhaberTeamCard = ({ employees = [], seats = {}, onAddEmployee, onManageCourses, onDeleteEmployee, onOpenCheckout, onUpgradeTier, licenseSyncing = false }) => {
  const total = seats.seats_total || 0;
  const used = seats.seats_used ?? employees.length;
  const free = seats.seats_free ?? Math.max(total - used, 0);
  const seatsFull = total > 0 && free <= 0;
  const noSubscription = total === 0;

  const handleBuySeats = () => {
    // Wenn ein Tier-Upgrade-Handler gesetzt ist (Flex/Pro → nächste Stufe),
    // öffne das Upgrade-Modal statt des generischen Basis-Checkouts.
    if (onUpgradeTier && onUpgradeTier({ used, total })) return;
    if (onOpenCheckout) onOpenCheckout('basis');
  };

  const handleAdd = () => (seatsFull ? handleBuySeats() : onAddEmployee());

  return (
    <Card className="bg-gray-900/50 border-gray-800 mb-8">
      <CardHeader className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-yellow-500" /> Monteure / Mitarbeiter verwalten
          </CardTitle>
          <CardDescription>Zentrales Firmen-Abo: Sie bezahlen, Ihre Monteure schulen sich kostenlos.</CardDescription>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="bg-black/40 border border-gray-800 rounded-lg px-3 py-2" data-testid="seats-counter">
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Lizenzen</div>
            <div className="text-white font-bold text-lg leading-tight" data-testid="seats-counter-text">
              {total > 0 ? (
                <>
                  <span className={seatsFull ? 'text-red-400' : 'text-white'}>{used}</span>
                  <span className="text-gray-400 font-normal text-sm"> von </span>
                  <span>{total}</span>
                  <span className="text-xs text-gray-400 ml-1 font-normal">Lizenzen belegt</span>
                </>
              ) : (
                <span className="text-gray-500">kein Abo</span>
              )}
            </div>
          </div>
          <Button
            onClick={handleAdd}
            className="bg-yellow-500 text-black hover:bg-yellow-400"
            data-testid="add-employee-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Mitarbeiter hinzufügen
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {licenseSyncing && (
          <div className="mb-4 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-xs text-blue-200" data-testid="license-sync-banner">
            Ihre neue Lizenz wird gerade von Digistore24 bestätigt – das dauert meist nur wenige Sekunden …
          </div>
        )}
        {(seatsFull || noSubscription) && (
          <div className="mb-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-start gap-2" data-testid="seats-warning">
            <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
            <div className="text-xs text-yellow-200/90 flex-1">
              {noSubscription
                ? 'Sie haben noch kein aktives Lizenz-Paket. Bitte buchen Sie ein Abo, um Mitarbeiter freizuschalten.'
                : `Alle ${total} Lizenzen sind vergeben. Buchen Sie zusätzliche Lizenzen oder entfernen Sie einen Mitarbeiter, um Platz freizugeben.`}
            </div>
            <Button
              size="sm"
              className="bg-yellow-500 text-black hover:bg-yellow-400 shrink-0"
              onClick={handleBuySeats}
              data-testid="buy-more-seats-btn"
            >
              <ShoppingCart className="w-3.5 h-3.5 mr-1.5" /> {noSubscription ? 'Lizenzen buchen' : 'Lizenz hinzubuchen'}
            </Button>
          </div>
        )}
        {employees.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Noch keine Mitarbeiter vorhanden</p>
            <Button onClick={handleAdd} className="mt-4" variant="outline" data-testid="add-first-employee-btn">
              Ersten Mitarbeiter anlegen
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {employees.map((emp) => (
              <div
                key={emp.id}
                className="flex items-center justify-between bg-black/30 p-4 rounded-lg border border-gray-800 hover:border-yellow-500/30 transition-colors gap-2"
                data-testid={`employee-row-${emp.id}`}
              >
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <div className="h-10 w-10 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-500 font-bold shrink-0">
                    {emp.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{emp.name}</p>
                    <p className="text-sm text-gray-400 truncate">{emp.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-center hidden sm:block">
                    <p className="text-2xl font-bold text-white leading-none">{emp.totalCourses}</p>
                    <p className="text-[10px] text-gray-500 uppercase">Kurse</p>
                  </div>
                  <div className="text-center hidden sm:block">
                    <p className="text-2xl font-bold text-green-500 leading-none">{emp.completedCourses}</p>
                    <p className="text-[10px] text-gray-500 uppercase">Fertig</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
                    onClick={() => onManageCourses(emp)}
                    data-testid={`manage-courses-btn-${emp.id}`}
                  >
                    Kurse <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                  {onDeleteEmployee && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => onDeleteEmployee(emp)}
                      data-testid={`delete-employee-btn-${emp.id}`}
                      title="Mitarbeiter entfernen & Lizenz freigeben"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
